import styled, { css } from 'styled-components';
import style from 'styles/styled-components/style';
import CoreLoading from 'shared/CoreLoading';
import CoreSkeletonBox from 'shared/CoreSkeletonBox';
import { format } from 'date-fns';
import { PlatformCode } from 'type/common';
import { useGetPreviewQuery } from 'hooks/queries/match';
import CoreImg from 'shared/CoreImg';
import { dateView } from 'utils/date';
import useCommonStore from 'store/useCommonStore';

const preview_size: Record<PlatformCode, { height: number; width: number }> = {
	ig: { height: 366, width: 330 },
	yt: { height: 370, width: 350 },
	nb: { height: 340, width: 312 },
	tt: { height: 308, width: 378 },
};

interface IPreviewPosting {
	platform_pk: string;
	target: HTMLDivElement;
	is_displayed: boolean;
}

interface IPreviewYt {
	thumbnail: string;
	title: string;
	view_count: number;
	published_at: string;
}

interface IPreviewNb {
	thumbnail: string;
	title: string;
	published_at: string;
}

/**
 * 찾기 페이지에서 아이콘 hover 시 띄워주는 preview 창
 * @param props.platform_pk 프리뷰 대상의 pk
 * @param props.target hover 할 아이콘
 * @param props.is_displayed 프리뷰 창이 보여질 지의 여부
 */
const PreviewPosting = ({ platform_pk, target, is_displayed }: IPreviewPosting) => {
	const { platform } = useCommonStore();
	const platform_code = platform.code as PlatformCode;

	/** preview 창의 위치를 잡는 함수 */
	const setPosition = (): [number, number, 'up' | 'down'] => {
		if (!target) return [0, 0, 'down'];
		let type: 'up' | 'down';

		const target_rect = target.getBoundingClientRect();
		const target_bottom = window.innerHeight - target_rect.bottom;
		const position_margin = 13;
		const position_margin_remain = target.offsetHeight - position_margin;

		if (
			target_bottom + position_margin_remain < preview_size[platform_code].height &&
			target_rect.top + position_margin_remain < preview_size[platform_code].height
		)
			type = 'down';
		else if (target_bottom + position_margin_remain < preview_size[platform_code].height)
			type = 'up';
		else type = 'down';

		return [target_rect.left + position_margin, target_rect.top + position_margin, type];
	};

	const { data: preview_data = null, isLoading: preview_loading } = useGetPreviewQuery(
		platform_pk,
		is_displayed,
	);

	/** 날짜 포맷 */
	const formatter = (date: string) => format(new Date(date), 'yyyy.MM.dd');

	if (is_displayed)
		return (
			<PreviewBox
				_left={setPosition()[0]}
				_top={setPosition()[1]}
				position={setPosition()[2]}
				platform={platform_code}
			>
				{platform_code === 'ig' && (
					<>
						<PreviewTitle>
							<style.Text typo="p_bold">
								최근 포스팅
								{preview_data?.contents.length > 0
									? ` ${preview_data?.contents.length}개`
									: ''}
							</style.Text>
							<style.Text typo="span">
								{preview_data?.created_at
									? `${formatter(preview_data?.created_at)} 기준`
									: ''}
							</style.Text>
						</PreviewTitle>
						<PreviewPictureBox>
							{/* 로드 완료 */}
							{preview_data?.contents.length > 0 &&
								preview_data.contents.map((picture: string, idx: number) => (
									<PreviewPictureItem key={idx}>
										<CoreImg
											src={picture ?? ''}
											alt="instagram-posting-preview"
											error_src="/image/small_no_image.png"
										/>
									</PreviewPictureItem>
								))}
							{/* 결과 없음 */}
							{preview_data?.contents.length === 0 && (
								<style.Grid flex="center" _height="100%">
									<style.Text typo="span">최근 포스팅을 가져올 수 없습니다.</style.Text>
								</style.Grid>
							)}
							{/* 로딩 */}
							{(preview_data === null || preview_loading) && (
								<style.Grid flex="center" _height="100%">
									<CoreLoading color_type="black" />
								</style.Grid>
							)}
						</PreviewPictureBox>
					</>
				)}

				{platform_code === 'yt' && (
					<>
						<PreviewTitle>
							<style.Text typo="p_bold">최근 영상</style.Text>
							<style.Text typo="span">
								{preview_data?.created_at
									? `${formatter(preview_data?.created_at)} 기준`
									: ''}
							</style.Text>
						</PreviewTitle>

						<VideoList>
							{/* 로드 완료 */}
							{preview_data?.contents?.length > 0 &&
								preview_data?.contents.map((item: IPreviewYt, idx: number) => (
									<VideoItem key={idx}>
										<Thumbnail>
											<CoreImg
												src={item.thumbnail}
												_width={160}
												_height={90}
												alt="youtube-thumbnail-preview"
												error_src="/image/small_no_image.png"
											/>
										</Thumbnail>

										<VideoContentText>
											<style.Grid _height="65px">
												<VideoTitle typo="span">{item.title}</VideoTitle>
											</style.Grid>

											<style.Grid _height="21px">
												<GrayText typo="span">
													{item.view_count}회 ・ {dateView(item.published_at)}
												</GrayText>
											</style.Grid>
										</VideoContentText>
									</VideoItem>
								))}
							{/* 로딩 */}
							{(preview_data === null || preview_loading) &&
								new Array(3).fill(null).map((item, idx) => (
									<VideoItem key={idx}>
										<Thumbnail>
											<CoreSkeletonBox
												_width="100%"
												_height="100%"
												delay={`${idx * 1}s`}
											/>
										</Thumbnail>

										<VideoContentText>
											<CoreSkeletonBox
												_width="100%"
												_height="16px"
												delay={`${idx * 1}s`}
											/>
											<CoreSkeletonBox
												_width="60%"
												_height="16px"
												delay={`${idx * 1}s`}
											/>
										</VideoContentText>
									</VideoItem>
								))}
							{/* 결과 없음 */}
							{preview_data && Object.keys(preview_data).length === 0 && (
								<style.Grid flex="center" _height="100%">
									<style.Text typo="span">최근 영상을 가져올 수 없습니다.</style.Text>
								</style.Grid>
							)}
						</VideoList>
					</>
				)}

				{platform_code === 'nb' && (
					<>
						<PreviewTitle>
							<style.Text typo="p_bold">최근 게시물</style.Text>
							<style.Text typo="span">
								{preview_data?.created_at
									? `${formatter(preview_data?.created_at)} 기준`
									: ''}
							</style.Text>
						</PreviewTitle>
						<PostingList>
							{/* 로드 완료 */}
							{preview_data?.contents.length > 0 &&
								preview_data?.contents?.map((item: IPreviewNb, idx: number) => (
									<PostingItem key={idx + 1}>
										<PostingContentText>
											<PostingDate>{formatter(item?.published_at)}</PostingDate>

											<PostingTitle typo="span">{item?.title}</PostingTitle>
										</PostingContentText>

										<PostingThumbnail>
											<CoreImg
												src={item.thumbnail}
												_width={80}
												_height={80}
												alt="naver-posting-preview"
												error_src="/image/small_no_image.png"
											/>
										</PostingThumbnail>
									</PostingItem>
								))}
							{/* 로딩 */}
							{(preview_data === null || preview_loading) &&
								new Array(3).fill(null).map((item, idx) => (
									<VideoItem key={idx}>
										<PostingContentText>
											<CoreSkeletonBox
												_width="100%"
												_height="16px"
												delay={`${idx * 1}s`}
											/>
											<CoreSkeletonBox
												_width="60%"
												_height="16px"
												delay={`${idx * 1}s`}
											/>
										</PostingContentText>

										<PostingThumbnail>
											<CoreSkeletonBox
												_width="100%"
												_height="100%"
												delay={`${idx * 1}s`}
											/>
										</PostingThumbnail>
									</VideoItem>
								))}
							{/* 결과 없음 */}
							{preview_data && Object.keys(preview_data).length === 0 && (
								<style.Grid flex="center" _height="100%">
									<style.Text typo="span">최근 게시물을 가져올 수 없습니다.</style.Text>
								</style.Grid>
							)}
						</PostingList>
					</>
				)}
				{platform_code === 'tt' && (
					<>
						<PreviewTitle>
							<style.Text typo="p_bold">최근 영상</style.Text>
							<style.Text typo="span">
								{preview_data?.created_at
									? `${formatter(preview_data?.created_at)} 기준`
									: ''}
							</style.Text>
						</PreviewTitle>

						<ShortVideoList>
							{preview_data?.contents?.length > 0 &&
								preview_data?.contents.map((item: IPreviewYt, idx: number) => (
									<ShortVideoItem key={idx}>
										<ShortVideoThumbnail>
											{item.thumbnail && (
												<CoreImg
													src={item.thumbnail}
													_width={110}
													_height={146}
													alt="tiktok-preview"
													error_src="/image/small_no_image.png"
												/>
											)}
										</ShortVideoThumbnail>

										<TiktokContentText>
											<style.Grid _height="21px">
												<GrayText typo="span">
													{item.view_count}회 ・{' '}
													{item.published_at ? dateView(item.published_at) : ''}
												</GrayText>
											</style.Grid>

											<style.Grid _height="65px">
												<VideoTitle typo="span">{item.title}</VideoTitle>
											</style.Grid>
										</TiktokContentText>
									</ShortVideoItem>
								))}
							{(preview_data === null || preview_loading) &&
								new Array(3).fill(null).map((item, idx) => (
									<ShortVideoItem key={idx}>
										<ShortVideoThumbnail>
											<CoreSkeletonBox
												_width="100%"
												_height="100%"
												delay={`${idx * 1}s`}
											/>
										</ShortVideoThumbnail>

										<VideoContentText>
											<CoreSkeletonBox
												_width="100%"
												_height="16px"
												delay={`${idx * 1}s`}
											/>
											<CoreSkeletonBox
												_width="60%"
												_height="16px"
												delay={`${idx * 1}s`}
											/>
										</VideoContentText>
									</ShortVideoItem>
								))}
							{preview_data && Object.keys(preview_data).length === 0 && (
								<style.Grid flex="center" _height="100%">
									<style.Text typo="span">최근 영상을 가져올 수 없습니다.</style.Text>
								</style.Grid>
							)}
						</ShortVideoList>
					</>
				)}
			</PreviewBox>
		);
	return null;
};

const PreviewBox = styled.div<{
	_left?: number;
	_top?: number;
	position?: 'up' | 'down';
	platform: PlatformCode;
}>`
	${({ platform }) => css`
		width: ${preview_size[platform].width}px;
		height: ${preview_size[platform].height}px;
	`}
	padding: 16px;
	border: 1px solid ${({ theme }) => theme.colors.gray.gray20};
	border-radius: 8px;
	background-color: ${({ theme }) => theme.colors.gray.gray_white};
	display: flex;
	flex-direction: column;
	position: fixed;
	top: ${({ _top }) => `${_top}px`};
	left: ${({ _left }) => `${_left}px`};
	${({ position }) => position === 'up' && `transform: translateY(-100%)`};
	z-index: 10;
	cursor: auto;

	pointer-events: none;

	& > p {
		${({ theme }) => theme.typo.body.p_bold};
		margin-bottom: 8px;
	}
`;

// platform === 'ig'
const PreviewPictureBox = styled.div`
	background-color: ${({ theme }) => theme.colors.gray.gray05_bg};
	border-radius: 8px;
	overflow: hidden;
	width: 100%;
	height: 100%;
	${({ theme }) => theme.common.flexStart};
	align-items: flex-start;
	align-content: flex-start;
	flex-wrap: wrap;
`;

const PreviewTitle = styled.div`
	width: 100%;
	margin-bottom: 8px;

	${({ theme }) => theme.common.flexBetween};
	align-items: flex-end;
`;

const PreviewPictureItem = styled.div`
	width: calc(100% / 3);
	height: calc(100% / 3);
	background-color: ${({ theme }) => theme.colors.gray.gray15};
	background-repeat: no-repeat;
	background-position: center;
	background-size: cover;
	overflow: hidden;
`;

// platform === 'yt'
const VideoList = styled.ul`
	width: 100%;
	height: 100%;

	${({ theme }) => theme.common.flexStart};
	flex-direction: column;
	gap: 16px;
`;

const VideoItem = styled.li`
	width: 100%;

	${({ theme }) => theme.common.flexBetween};
	align-items: stretch;
`;

const Thumbnail = styled.div`
	width: 160px;
	height: 90px;
	background-color: ${({ theme }) => theme.colors.gray.gray10};
`;

const VideoContentText = styled.div`
	/* width: 120px; */
	width: calc(100% - 160px);
	/* width: 100%; */
	padding: 0 0 0 8px;

	${({ theme }) => theme.common.flexStart}
	align-items: flex-start;
	flex-direction: column;
	gap: 4px;
`;

const VideoTitle = styled(style.Text)`
	width: 100%;
	height: 65px;
	white-space: pre-wrap;
	${({ theme }) => theme.common.ellipsis_multi(3)};
`;

const GrayText = styled(style.Text)`
	color: ${({ theme }) => theme.colors.gray.gray30};
`;

// platform === 'nb'
const PostingList = styled(VideoList)`
	margin-top: 8px;
	gap: 16px;
`;

const PostingItem = styled.div`
	width: 100%;

	${({ theme }) => theme.common.flexBetween};
	align-items: stretch;
`;

const PostingThumbnail = styled.div`
	width: 80px;
	height: 80px;
	border-radius: 8px;
	overflow: hidden;
	background-color: ${({ theme }) => theme.colors.gray.gray10};
`;

const PostingContentText = styled.div`
	width: 184px;
	padding-bottom: 4px;

	${({ theme }) => theme.common.flexStart}
	align-items: flex-start;
	flex-direction: column;
	gap: 8px;
`;

const PostingDate = styled(style.Text)`
	width: 100%;
	${({ theme }) => theme.typo.body.span};
	color: ${({ theme }) => theme.colors.gray.gray30};
`;

const PostingTitle = styled(style.Text)`
	width: 100%;
	white-space: pre-wrap;
	${({ theme }) => theme.common.ellipsis_multi(2)};
`;

// platform === 'tt'
const ShortVideoThumbnail = styled.div`
	width: 110px;
	height: 146px;
	background-color: ${({ theme }) => theme.colors.gray.gray10};
`;

const ShortVideoList = styled.ul`
	width: 100%;
	height: 100%;

	${({ theme }) => theme.common.flexStart};
	gap: 8px;
`;

const ShortVideoItem = styled.li`
	width: 110px;
	height: 100%;

	${({ theme }) => theme.common.flexBetween};
	flex-direction: column;
	/* align-items: stretch; */
`;

const TiktokContentText = styled.div`
	width: 110px;
	padding: 0 4px;

	${({ theme }) => theme.common.flexStart}
	align-items: flex-start;
	flex-direction: column;
	gap: 4px;
`;

export default PreviewPosting;
